import { api } from ".";
import type { KyResponse } from "ky";

export type UserInfo = {
  id: string;
  full_name: string;
  email: string;
  is_verified: boolean;
  is_active: boolean;
  /** @deprecated use {@link org_admin} instead */
  is_admin: boolean;
  org_admin: boolean;
  is_superadmin: boolean;
  organization_id: string | null;
  role_id: number;
  department_id: number;
  /** Date string */
  created_at: string;
  /** Date string */
  updated_at: null | string;

  /**
   * Mapping of resources to allowed actions.
   *
   * `all` means everything is allowed, be it all actions for a given resource or all resources
   */
  permissions: Record<string, Array<string>>;
  profile_picture_url?: string | null;
  user_type?: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email_verified_at: string | null;
  status: "pending_verification" | "active" | "suspended";
};

function me(): Promise<KyResponse<UserInfo>> {
  return api.auth.raw.get<UserInfo>("api/v1/auth/me");
}

function changePassword(props: { current: string; new: string }) {
  return api.auth.raw.post("api/v1/auth/change-password", {
    json: {
      current_password: props.current,
      new_password: props.new,
    },
  });
}

function updatePassword(props: { token: string; password: string }) {
  return api.auth.raw.post("api/v1/auth/update-password", {
    json: {
      token: props.token,
      new_password: props.password,
    },
  });
}

export type ProfilePictureResponse = {
  picture_url?: string;
  profile_picture_url?: string;
  url?: string;
};

function normalizeUrl(url: string | null | undefined): string | null {
  if (typeof url !== "string") return null;
  const trimmed = url.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function getProfileInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function resolveProfilePictureUrl(
  data: ProfilePictureResponse | UserInfo | null | undefined,
): string | null {
  if (!data) return null;
  const fromProfile = normalizeUrl(data.profile_picture_url);
  if (fromProfile) return fromProfile;
  if ("picture_url" in data) {
    const fromPicture = normalizeUrl(data.picture_url);
    if (fromPicture) return fromPicture;
  }
  if ("url" in data) {
    return normalizeUrl(data.url);
  }
  return null;
}

function uploadProfilePicture(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return api.auth.raw
    .post("api/v1/auth/me/picture", { body: formData })
    .json<UserInfo>();
}

async function deleteProfilePicture(): Promise<UserInfo | null> {
  const response = await api.auth.raw.delete("api/v1/auth/me/picture");
  if (response.status === 204) return null;
  return response.json<UserInfo>();
}

export default {
  me,
  changePassword,
  updatePassword,
  uploadProfilePicture,
  deleteProfilePicture,
};
